import React, { useState } from "react";
import { Link } from "react-router-dom";

import Button from "ui/Button";
import { IncidentIoLogo } from "ui/Icons";
import ModalContainer from "ui/ModalContainer";
import Panel from "ui/Panel";
import PanelSection from "ui/PanelSection";
import { useFeature } from "providers/OrganizationFeatures";
import { useSetFlash } from "providers/WithFlashes";
import { useRoutes } from "utils/routes";
import { useAppConfig } from "providers/WithAppConfig";

import { OrganizationIntegrationsQuery } from "../gql/Query.generated";
import { EndpointList, useSelectedEndpoint } from "../Webhooks";
import IncidentIoEndpointModal from "./EndpointDetail";
import IncidentIoForm from "./IncidentIoForm";

type IncidentIoEndpointType =
  OrganizationIntegrationsQuery["getOrganizationDetails"]["incidentIoEndpoints"][number];

function IncidentIoPanel({
  organizationSlug,
  endpoints,
  canEdit,
}: {
  organizationSlug: string;
  endpoints: IncidentIoEndpointType[];
  canEdit: boolean;
}) {
  const hasIncidentIo = useFeature("incidentIo");
  const config = useAppConfig();
  const { organizationAlertDestinations, organizationIntegrationsIncidentIo } =
    useRoutes();
  const [adding, setAdding] = useState(false);
  const setFlash = useSetFlash();
  const { selectedEndpoint, closeEndpoint, endpointRemoved } =
    useSelectedEndpoint(organizationSlug, endpoints, "incidentIoEndpointId");

  if (!hasIncidentIo && endpoints.length === 0) {
    return null;
  }

  if (!config?.webhooksAvailable) {
    return (
      <Panel title="incident.io">
        <PanelSection>
          <p className="mb-0">
            incident.io alert sources require encrypted credential storage,
            which is not configured for this pganalyze installation. Set{" "}
            <code>ENCRYPTION_PRIMARY_KEY</code> and{" "}
            <code>ENCRYPTION_KEY_DERIVATION_SALT</code> to enable them.
          </p>
        </PanelSection>
      </Panel>
    );
  }

  return (
    <Panel title="incident.io">
      <PanelSection>
        <p className="mb-0">
          Send alerts to incident.io as alert events. Create an HTTP alert
          source in incident.io, add its URL and token here, then pick it as a
          target in an{" "}
          <Link to={organizationAlertDestinations(organizationSlug)}>
            alert destination
          </Link>
          .
        </p>
      </PanelSection>
      {endpoints.length > 0 && (
        <EndpointList
          organizationSlug={organizationSlug}
          endpoints={endpoints}
          endpointPath={organizationIntegrationsIncidentIo}
        />
      )}
      <PanelSection>
        {endpoints.length === 0 && (
          <div className="mb-2 text-[#606771]">
            No incident.io alert sources configured yet.
          </div>
        )}
        {canEdit ? (
          <Button onClick={() => setAdding(true)}>
            <span className="flex items-center justify-center gap-1.5 px-3">
              <IncidentIoLogo alt="" />
              Add Alert Source
            </span>
          </Button>
        ) : (
          <>
            You are not allowed to edit integration settings for this
            organization.
          </>
        )}
      </PanelSection>
      {adding && (
        <ModalContainer
          title="Add incident.io Alert Source"
          layout="centered"
          className="!min-w-0"
          onClose={() => setAdding(false)}
        >
          <IncidentIoForm
            organizationSlug={organizationSlug}
            onSaved={(message) => {
              setFlash("success", message);
              setAdding(false);
            }}
            onCancel={() => setAdding(false)}
          />
        </ModalContainer>
      )}
      {selectedEndpoint && (
        <IncidentIoEndpointModal
          key={selectedEndpoint.id}
          organizationSlug={organizationSlug}
          endpoint={selectedEndpoint}
          canEdit={canEdit}
          onClose={closeEndpoint}
          onRemoved={() =>
            endpointRemoved(
              `incident.io alert source "${selectedEndpoint.name}" removed`,
            )
          }
        />
      )}
    </Panel>
  );
}

export default IncidentIoPanel;
